// ============================================================================
//  PLATFORM DETECTION UTILITY

// ============================================================================
// Detects whether app is running on web, iOS, or Android
// Used for platform-specific behavior (OAuth, notifications, etc.)
// ============================================================================

import { Capacitor } from '@capacitor/core';

export type Platform = 'web' | 'ios' | 'android';

/**
 * Get the current platform
 */
export const getPlatform = (): Platform => {
  if (Capacitor.isNativePlatform()) {
    return Capacitor.getPlatform() as 'ios' | 'android';
  }
  return 'web';
};

/**
 * Check if running on a native mobile platform (iOS or Android)
 */
export const isNative = (): boolean => {
  return Capacitor.isNativePlatform();
};

/**
 * Check if running on iOS
 */
export const isIOS = (): boolean => {
  return Capacitor.getPlatform() === 'ios';
};

/**
 * Check if running on Android
 */
export const isAndroid = (): boolean => {
  return Capacitor.getPlatform() === 'android';
};

/**
 * Check if running on web (browser)
 */
export const isWeb = (): boolean => {
  return !Capacitor.isNativePlatform();
};

/**
 * Check if the device is a mobile device (even in browser)
 * This detects mobile browsers, not just native apps
 */
export const isMobileDevice = (): boolean => {
  if (isNative()) return true;
  
  // Check user agent for mobile browsers
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
};

/**
 * Check if device supports touch
 */
export const isTouchDevice = (): boolean => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

/**
 * Get safe area insets (for notches, home indicators)
 * Returns CSS env() values or fallbacks
 */
export const getSafeAreaInsets = () => {
  return {
    top: 'env(safe-area-inset-top, 0px)',
    right: 'env(safe-area-inset-right, 0px)',
    bottom: 'env(safe-area-inset-bottom, 0px)',
    left: 'env(safe-area-inset-left, 0px)',
  };
};

/**
 * Platform-specific configuration
 */
export const platformConfig = {
  // OAuth method: popup works on web, redirect needed for native
  useRedirectAuth: isNative(),
  
  // Haptic feedback available on native
  hapticFeedbackEnabled: isNative(),
  
  // Status bar management available on native
  statusBarEnabled: isNative(),
  
  // Push notifications setup differs by platform
  pushNotificationsEnabled: isNative(),
};

export default {
  getPlatform,
  isNative,
  isIOS,
  isAndroid,
  isWeb,
  isMobileDevice,
  isTouchDevice,
  getSafeAreaInsets,
  platformConfig,
};